import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  WarningAmber,
} from '@mui/icons-material';
import { metricsService } from '../../services/metricsService';
import { dashboardService } from '../../services/dashboardService';
import { useSiteStore } from '../../store/site.store';
import { useAuthStore } from '../../store/auth.store';
import {
  buildAgencyHealthBreakdown,
  buildAgencyHeaderSummary,
  buildAgencyKpiCards,
} from './agencyDashboardData';

function toNumber(value: unknown, fallback = 0) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

export default function AgencyDashboard() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const setSites = useSiteStore((state) => state.setSites);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardPayload, setDashboardPayload] = useState<Awaited<ReturnType<typeof dashboardService.getAgencyDashboard>> | null>(null);
  const [filters, setFilters] = useState({ customer: 'all', site: 'all', search: '', health: 'all', status: 'all', dateRange: { start: '', end: '' } });
  const [viewMode, setViewMode] = useState<'customers' | 'websites'>('customers');
  const [selectedSiteDetail, setSelectedSiteDetail] = useState<Awaited<ReturnType<typeof dashboardService.getAgencyDashboard>>['sites'][number] | null>(null);
  const effectiveTenantId = user?.tenantId;

  useEffect(() => {
    let isMounted = true;

    const loadSites = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedSites = await metricsService.getSites();
        const scopeTenantId = user?.tenantId;
        if (!isMounted) {
          return;
        }

        let loadedPayload: Awaited<ReturnType<typeof dashboardService.getAgencyDashboard>> | null = null;
        if (scopeTenantId) {
          loadedPayload = await dashboardService.getAgencyDashboard(scopeTenantId);
          if (isMounted) {
            setDashboardPayload(loadedPayload);
          }
        }

        setSites(fetchedSites);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error('Failed to load agency dashboard sites', err);
        setError('Unable to load your customer portfolio right now.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSites();

    return () => {
      isMounted = false;
    };
  }, [effectiveTenantId, setSites, user?.proxyTenantId, user?.tenantId]);

  const dashboardSummary = dashboardPayload?.summary;
  const dashboardSites = useMemo(() => dashboardPayload?.sites ?? [], [dashboardPayload?.sites]);
  const dashboardAnomalies = useMemo(() => dashboardPayload?.anomalies ?? [], [dashboardPayload?.anomalies]);
  const dashboardTrendPoints = useMemo(() => dashboardPayload?.search_performance ?? [], [dashboardPayload?.search_performance]);
  const dashboardSeoScoreTrendPoints = useMemo(() => dashboardPayload?.seo_score_trend ?? [], [dashboardPayload?.seo_score_trend]);
  const dashboardCustomerRows = useMemo(() => dashboardPayload?.customer_portfolio_rows ?? [], [dashboardPayload?.customer_portfolio_rows]);

  const normalizedDashboardSites = useMemo(
    () =>
      dashboardSites.map((site) => ({
        id: site.id,
        tenantId: String(site.tenant_id ?? ''),
        website: site.site_name || site.name || site.url || `Site ${site.id}`,
        customer: site.customer_id != null && String(site.customer_id) !== '0' ? `Customer ${site.customer_id}` : `Tenant ${site.tenant_id ?? site.id}`,
        score: toNumber(site.score, 0),
        healthBucket: site.health_bucket,
        criticalIssues: toNumber(site.critical_issues, 0),
        openIssues: toNumber(site.open_issues, 0),
        traffic: toNumber(site.traffic, 0),
        clicks: toNumber(site.clicks, 0),
      })),
    [dashboardSites],
  );

  const customerCount = toNumber(dashboardSummary?.customer_count, 0);
  const totalSites = toNumber(dashboardSummary?.website_count, 0);
  const healthySites = toNumber(dashboardSummary?.healthy_site_count, 0);
  const needsAttentionSites = toNumber(dashboardSummary?.needs_attention_site_count, 0);
  const criticalSignals = toNumber(dashboardSummary?.critical_site_count, 0);
  const averageHealthScore = toNumber(dashboardSummary?.average_score, 0);
  const agencyLabel = user?.agencyName || user?.tenantName || 'Agency';

  const agencyHeaderSummary = useMemo(
    () => buildAgencyHeaderSummary({ agencyName: agencyLabel, customerCount, siteCount: totalSites }),
    [agencyLabel, customerCount, totalSites]
  );

  const getHealthDisplayLabel = (bucket?: string) => {
    const normalized = String(bucket ?? '').trim().toLowerCase();
    if (normalized === 'critical') return 'Critical';
    if (normalized === 'needs_attention' || normalized === 'needs attention' || normalized === 'issues') return 'Needs Attention';
    return 'Healthy';
  };

  const healthBreakdown = useMemo(() => {
    const distribution = dashboardPayload?.health_distribution;
    return buildAgencyHealthBreakdown({
      healthy: toNumber(distribution?.healthy, healthySites),
      good: toNumber(distribution?.good, 0),
      issues: toNumber(distribution?.issues, needsAttentionSites),
      critical: toNumber(distribution?.critical, criticalSignals),
    });
  }, [criticalSignals, dashboardPayload, healthySites, needsAttentionSites]);

  const backendClicks = toNumber(dashboardSummary?.clicks, 0);
  const backendImpressions = toNumber(dashboardSummary?.impressions, 0);
  const backendCtr = backendImpressions > 0 ? `${((backendClicks / backendImpressions) * 100).toFixed(1)}%` : '0%';
  const agencyKpis = useMemo(
    () =>
      buildAgencyKpiCards({
        customerCount,
        siteCount: totalSites,
        criticalSites: criticalSignals,
        openIssues: toNumber(dashboardSummary?.open_issue_count, 0),
        averageHealthScore,
        gscClicks: backendClicks,
        gscImpressions: backendImpressions,
        ctr: backendCtr,
      }),
    [averageHealthScore, backendClicks, backendCtr, backendImpressions, criticalSignals, customerCount, dashboardSummary?.open_issue_count, totalSites]
  );

  const agencyTableRows = useMemo(() => {
    if (dashboardCustomerRows.length > 0) {
      return dashboardCustomerRows.map((row, index) => ({
        customer: row.customer || `Customer ${index + 1}`,
        avgSeoScore: toNumber(row.avgSeoScore, 0),
        openIssues: toNumber(row.openIssues, 0),
      }));
    }

    return [];
  }, [dashboardCustomerRows]);

  const websiteTableRows = useMemo(() => {
    if (normalizedDashboardSites.length > 0) {
      return normalizedDashboardSites.map((site) => ({
        id: site.id,
        customer: site.customer,
        website: site.website,
        seoScore: site.score,
        health: getHealthDisplayLabel(site.healthBucket),
      }));
    }

    return [];
  }, [getHealthDisplayLabel, normalizedDashboardSites]);

  const trendSeries = useMemo(() => {
    if (dashboardTrendPoints.length > 0) {
      return dashboardTrendPoints.map((point) => ({
        name: point.date || '',
        clicks: toNumber(point.clicks, 0),
        impressions: toNumber(point.impressions, 0),
      }));
    }

    return [];
  }, [dashboardTrendPoints]);

  const seoScoreTrendSeries = useMemo(() => {
    if (dashboardSeoScoreTrendPoints.length > 0) {
      return dashboardSeoScoreTrendPoints.map((point) => ({
        name: point.date || '',
        score: Number(point.score ?? 0),
      }));
    }

    return [];
  }, [dashboardSeoScoreTrendPoints]);

  const attentionItems = useMemo(
    () => {
      if (dashboardAnomalies.length > 0) {
        return dashboardAnomalies.slice(0, 5).map((issue) => {
          const matchingSite = dashboardSites.find((site) => Number(site.id) === Number(issue.site_id));
          return {
            customer: matchingSite?.site_name || matchingSite?.name || agencyLabel,
            site: issue.title || issue.metric,
            severity: String(issue.severity || 'medium').toLowerCase() === 'critical' ? 'Critical' : 'Needs review',
            detail: issue.description || issue.page || issue.status,
            key: String(issue.id),
          };
        });
      }

      return [];
    },
    [agencyLabel, dashboardAnomalies, dashboardSites]
  );

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading agency view...</Typography>
        </Stack>
      </Box>
    );
  }

  const healthChartData = [
    { name: 'Healthy', value: Math.max(0, healthBreakdown.find((item) => item.label === 'Healthy')?.value || 0), color: '#2e7d32' },
    { name: 'Good', value: Math.max(0, healthBreakdown.find((item) => item.label === 'Good')?.value || 0), color: '#0288d1' },
    { name: 'Needs Attention', value: Math.max(0, healthBreakdown.find((item) => item.label === 'Needs Attention')?.value || 0), color: '#f57c00' },
    { name: 'Critical', value: Math.max(0, healthBreakdown.find((item) => item.label === 'Critical')?.value || 0), color: '#d32f2f' },
  ].filter((item) => item.value > 0);

  return (
    <Box sx={{ p: 3 }}>
      <Dialog open={Boolean(selectedSiteDetail)} onClose={() => setSelectedSiteDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="span">{selectedSiteDetail?.site_name || selectedSiteDetail?.name || 'Site details'}</Typography>
            <IconButton edge="end" aria-label="close" onClick={() => setSelectedSiteDetail(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSiteDetail ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">Site URL</Typography>
                <Typography variant="body1">{selectedSiteDetail.site_url || selectedSiteDetail.url || '—'}</Typography>
              </Box>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Score</Typography>
                  <Typography variant="h6">{Number(selectedSiteDetail.score ?? 0).toFixed(1)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Health</Typography>
                  <Typography variant="h6">{getHealthDisplayLabel(selectedSiteDetail.health_bucket)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Open issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.open_issues ?? selectedSiteDetail.critical_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Critical issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.critical_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">High issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.high_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Medium issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.medium_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Low issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.low_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Clicks</Typography>
                  <Typography variant="body1">{selectedSiteDetail.clicks ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Impressions</Typography>
                  <Typography variant="body1">{selectedSiteDetail.impressions ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Traffic</Typography>
                  <Typography variant="body1">{selectedSiteDetail.traffic ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Trend</Typography>
                  <Typography variant="body1">{selectedSiteDetail.trend || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Agency ID</Typography>
                  <Typography variant="body1">{selectedSiteDetail.agency_id || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Tenant ID</Typography>
                  <Typography variant="body1">{selectedSiteDetail.tenant_id || '—'}</Typography>
                </Grid>
              </Grid>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>

      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flex: 1, width: '100%' }}>
          <TextField
            size="small"
            label="Search"
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            placeholder="Customer or website"
            sx={{ minWidth: 220, flex: 1 }}
          />
          <TextField
            size="small"
            label="Date"
            type="date"
            value={filters.dateRange.start}
            onChange={(event) => setFilters((current) => ({ ...current, dateRange: { ...current.dateRange, start: event.target.value } }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <Button variant="outlined">Filters</Button>
          <Button variant="contained">Add Client</Button>
        </Stack>
        <Button variant="outlined" onClick={() => navigate('/analytics')}>
          Customer dashboard
        </Button>
      </Stack>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {agencyHeaderSummary.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {agencyHeaderSummary.subtitle}
        </Typography>
      </Box>

      {error ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2}>
            {agencyKpis.slice(0, 7).map((card) => (
              <Grid item xs={12} sm={6} lg={3} key={card.label}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                    <Typography variant="h5" fontWeight="700" sx={{ mt: 0.5 }}>
                      {card.value}
                    </Typography>
                    {card.helperText ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {card.helperText}
                      </Typography>
                    ) : null}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600">Overall Site Health</Typography>
              <Box sx={{ height: 210 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={healthChartData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={2}>
                      {healthChartData.map((entry, index) => (
                        <Cell key={`${entry.name}-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={1} aria-label="Site health breakdown">
                {healthChartData.map((item) => (
                  <Stack key={item.name} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                      <Typography variant="body2">{item.name}</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>{item.value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="600">Customers / Websites</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant={viewMode === 'customers' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setViewMode('customers')}
                  >
                    Customers
                  </Button>
                  <Button
                    variant={viewMode === 'websites' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setViewMode('websites')}
                  >
                    Websites
                  </Button>
                </Stack>
              </Stack>
              <Box sx={{ overflowX: 'auto' }}>
                {viewMode === 'customers' ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e0e0e0' }}>Customer</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e0e0e0' }}>Score</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e0e0e0' }}>Issues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencyTableRows.map((row) => (
                        <tr key={row.customer}>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0' }}>{row.customer}</td>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0' }}>{row.avgSeoScore}</td>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0' }}>{row.openIssues}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e0e0e0' }}>Customer</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e0e0e0' }}>Website</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e0e0e0' }}>Score</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e0e0e0' }}>Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      {websiteTableRows.map((row) => (
                        <tr
                          key={`${row.customer}-${row.website}`}
                          onClick={() => setSelectedSiteDetail(dashboardSites.find((site) => Number(site.id) === Number(row.id)) ?? null)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0' }}>{row.customer}</td>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0' }}>{row.website}</td>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0' }}>{row.seoScore}</td>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0' }}>{row.health}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600">Needs Attention</Typography>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {attentionItems.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No immediate follow-up requirements were detected.</Typography>
                ) : (
                  attentionItems.map((item) => (
                    <Paper key={`${item.customer}-${item.site}`} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <WarningAmber color={item.severity === 'Critical' ? 'error' : 'warning'} fontSize="small" />
                        <Box>
                          <Typography variant="body2" fontWeight="600">{item.customer}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.site}</Typography>
                        </Box>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        {item.detail}
                      </Typography>
                    </Paper>
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600">Top Issues</Typography>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {attentionItems.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No issue rollups to show yet.</Typography>
                ) : (
                  attentionItems.map((item) => (
                    <Paper key={item.key} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight="600">{item.site}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.customer}</Typography>
                        </Box>
                        <Chip size="small" label={item.severity} color={item.severity === 'Critical' ? 'error' : 'warning'} />
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={7}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600">Google Search Performance Trend</Typography>
              <Box sx={{ height: 240, mt: 2 }}>
                {trendSeries.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <ChartTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="clicks" stroke="#1976d2" strokeWidth={2} name="Clicks" />
                      <Line type="monotone" dataKey="impressions" stroke="#2e7d32" strokeWidth={2} name="Impressions" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <Alert severity="info">No dated Google Search Console data is available.</Alert>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600">Current Search Performance</Typography>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">GSC Clicks</Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ mt: 0.5 }}>{backendClicks}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">GSC Impressions</Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ mt: 0.5 }}>{backendImpressions}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">CTR</Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ mt: 0.5 }}>{backendCtr}</Typography>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="600">SEO Score Trend</Typography>
              <Box sx={{ height: 220, mt: 2 }}>
                {seoScoreTrendSeries.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={seoScoreTrendSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, 100]} />
                      <ChartTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="score" stroke="#ff9800" strokeWidth={2} name="SEO Score" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <Alert severity="info">No SEO score history is available.</Alert>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
}
